/**
 * AccountantAutoSendCard
 * ----------------------
 * Settings card that lets a Profesional-tier user:
 *   - Toggle monthly automatic accountant sends
 *   - Pick the day of month (1–28) for the send
 *   - Give / revoke GDPR consent for sharing invoices with the accountant
 *
 * Auto-send requires: accountant_email set + consent given + tier = profesional.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserTier } from "@/hooks/useUserTier";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarClock, Loader2, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";

const AccountantAutoSendCard = () => {
  const { user } = useAuth();
  const { tierSlug } = useUserTier();
  const queryClient = useQueryClient();
  const isPro = tierSlug === "profesional";

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile-autosend", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select(
          "accountant_email, accountant_consent_at, accountant_auto_send_enabled, accountant_send_day",
        )
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const [enabled, setEnabled] = useState(false);
  const [day, setDay] = useState<number>(5);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setEnabled(!!profile.accountant_auto_send_enabled);
      setDay(profile.accountant_send_day ?? 5);
      setConsent(!!profile.accountant_consent_at);
    }
  }, [profile]);

  const hasEmail = !!profile?.accountant_email;
  const canEnable = isPro && hasEmail && consent;

  const save = async () => {
    if (!user) return;
    if (enabled && !canEnable) {
      if (!isPro) toast.error("Auto-send este disponibil doar pe planul Profesional.");
      else if (!hasEmail) toast.error("Adaugă mai întâi emailul contabilului.");
      else if (!consent) toast.error("Bifează acordul GDPR pentru a activa.");
      return;
    }

    setSaving(true);
    try {
      // Persist consent if newly given; clear it if user un-ticked
      let consentAt: string | null | undefined = undefined;
      let effectiveEnabled = enabled;
      if (consent && !profile?.accountant_consent_at) {
        consentAt = new Date().toISOString();
      } else if (!consent && profile?.accountant_consent_at) {
        consentAt = null;
        effectiveEnabled = false;
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          accountant_auto_send_enabled: effectiveEnabled,
          accountant_send_day: day,
          ...(consentAt !== undefined ? { accountant_consent_at: consentAt } : {}),
        })
        .eq("user_id", user.id);
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["profile-autosend", user.id] });
      queryClient.invalidateQueries({ queryKey: ["profile-accountant", user.id] });
      queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      toast.success("Setări salvate.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Eroare la salvare";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-primary" />
        <h3 className="font-heading font-semibold">Trimitere automată lunară</h3>
      </div>

      {!isPro && (
        <div className="rounded-lg border border-border bg-muted/30 p-3 flex gap-2 text-sm">
          <Lock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-muted-foreground">
            Disponibil pe planul{" "}
            <Link to="/select-plan" className="text-primary hover:underline font-medium">
              Profesional
            </Link>
            . Trimiterea manuală funcționează pe toate planurile.
          </p>
        </div>
      )}

      {!hasEmail && (
        <p className="text-xs text-muted-foreground">
          Completează mai sus emailul contabilului pentru a putea activa trimiterea automată.
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="autosend-toggle">Activează trimiterea lunară</Label>
              <p className="text-xs text-muted-foreground">
                Trimitem automat facturile lunii anterioare către contabil.
              </p>
            </div>
            <Switch
              id="autosend-toggle"
              checked={enabled}
              disabled={!isPro || !hasEmail}
              onCheckedChange={setEnabled}
            />
          </div>

          <div className="space-y-2">
            <Label>Ziua trimiterii</Label>
            <Select
              value={String(day)}
              onValueChange={(v) => setDay(Number(v))}
              disabled={!isPro || !hasEmail || !enabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    Ziua {d} a lunii
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Recomandat: ziua 5–10, după închiderea lunii anterioare.
            </p>
          </div>

          <label
            className={`flex gap-3 rounded-lg border border-border bg-muted/20 p-3 ${
              !isPro || !hasEmail ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
          >
            <Checkbox
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
              className="mt-0.5"
              disabled={!isPro || !hasEmail}
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              <ShieldCheck className="h-3.5 w-3.5 text-primary inline mr-1 -mt-0.5" />
              Sunt de acord ca <strong className="text-foreground">EZ Factura</strong> să
              transmită lunar facturile mele către contabilul setat, prin email, sub formă
              de arhivă ZIP. Pot retrage oricând acest acord debifând caseta. Detalii în{" "}
              <Link
                to="/privacy"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Politica de Confidențialitate
              </Link>
              .
            </span>
          </label>

          <Button
            onClick={save}
            disabled={saving || !isPro}
            className="w-full h-12 font-semibold"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Salvează Setările Auto-Send
          </Button>
        </>
      )}
    </div>
  );
};

export default AccountantAutoSendCard;
